import React from 'react';
import { 
  FiX, 
  FiVideo, 
  FiUser, 
  FiCalendar, 
  FiDollarSign, 
  FiStar,
  FiBookOpen,
  FiFileText,
  FiTag,
  FiClock,
  FiEye,
  FiDownload,
  FiShare2,
  FiCheckCircle,
  FiXCircle,
  FiAward,
  FiTrendingUp,
  FiUsers,
  FiMessageCircle,
  FiInfo,
  FiPlayCircle,
  FiImage,
  FiHash,
  FiList,
  FiGrid
} from "react-icons/fi";
import { 
  MdOutlineClose, 
  MdOutlineVideoLibrary,
  MdOutlinePerson,
  MdOutlineCalendarToday,
  MdOutlineAttachMoney,
  MdOutlineStar,
  MdOutlineBook,
  MdOutlineDescription,
  MdOutlineCategory,
  MdOutlineAccessTime,
  MdOutlineVisibility,
  MdOutlineDownload,
  MdOutlineShare,
  MdOutlineCheckCircle,
  MdOutlineCancel,
  MdOutlinePending,
  MdOutlinePlayCircle,
  MdOutlineImage,
  MdOutlineInfo,
  MdOutlineList
} from "react-icons/md";
import { 
  IoCloseOutline, 
  IoVideocamOutline,
  IoPersonOutline,
  IoCalendarOutline,
  IoCashOutline,
  IoStarOutline,
  IoBookOutline,
  IoDocumentTextOutline,
  IoTimeOutline,
  IoEyeOutline,
  IoDownloadOutline,
  IoShareOutline,
  IoCheckmarkCircle,
  IoCloseCircleOutline,
  IoPlayCircleOutline,
  IoImageOutline,
  IoInformationCircleOutline,
  IoListOutline
} from 'react-icons/io5';
import { AiFillStar } from "react-icons/ai";

const ContentViewModal = ({ title, onSubmit, onCancel, inputValue }) => {
  if (!inputValue) return null;

  // Format date
  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Status badge component
  const StatusBadge = ({ isDisable }) => {
    if (!isDisable) {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full">
          <FiCheckCircle className="w-3.5 h-3.5 text-emerald-500" />
          Active
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-rose-700 bg-rose-50 border border-rose-200 rounded-full">
        <FiXCircle className="w-3.5 h-3.5 text-rose-500" />
        Inactive
      </span>
    );
  };

  // Info row component
  const InfoRow = ({ icon: Icon, label, value, color = "text-gray-700" }) => (
    <div className="flex items-center justify-between p-2 bg-white rounded-lg border border-gray-100 hover:border-[#715EFE]/30 transition-colors duration-200">
      <div className="flex items-center gap-2">
        {Icon && <Icon className="w-4 h-4 text-gray-400" />}
        <span className="text-sm text-gray-500">{label}</span>
      </div>
      <span className={`text-sm font-medium ${color}`}>{value || 'N/A'}</span>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="w-full max-w-6xl bg-white rounded-2xl shadow-2xl max-h-[90vh] flex flex-col animate-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0 bg-gradient-to-r from-gray-50/50 to-white rounded-t-2xl">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-br from-[#855DF6]/10 to-[#715EFE]/10 rounded-xl">
              <FiBookOpen className="w-5 h-5 text-[#715EFE]" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-800">{title || 'Course Details'}</h2>
              <p className="text-xs text-gray-400 hidden sm:block">View course information</p>
            </div>
          </div>
          <button
            onClick={onCancel}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all duration-200"
          >
            <FiX className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            {/* Left Column - Media & Course Info */}
            <div className="lg:col-span-2 space-y-6">
              {/* Video Section */}
              {inputValue?.videoUrl && (
                <div className="bg-gray-50/80 rounded-xl border border-gray-200 overflow-hidden">
                  <div className="p-4 border-b border-gray-200 flex items-center gap-2">
                    <FiVideo className="w-4 h-4 text-[#715EFE]" />
                    <h3 className="text-sm font-semibold text-gray-700">Video Preview</h3>
                  </div>
                  <div className="p-4 bg-black">
                    <video
                      controls
                      poster={inputValue?.thumbUrl}
                      className="w-full h-auto max-h-[400px] object-contain rounded-lg"
                      src={inputValue?.videoUrl}
                      onContextMenu={(e) => e.preventDefault()}
                      disablePictureInPicture
                      controlsList="nodownload noplaybackrate"
                    >
                      Your browser does not support the video tag.
                    </video>
                  </div>
                </div>
              )}

              {/* Course Information */}
              <div className="bg-gray-50/80 rounded-xl border border-gray-200 overflow-hidden">
                <div className="p-4 border-b border-gray-200 flex items-center gap-2">
                  <FiInfo className="w-4 h-4 text-[#715EFE]" />
                  <h3 className="text-sm font-semibold text-gray-700">Course Information</h3>
                </div>
                <div className="p-4 space-y-4">
                  <div>
                    <p className="text-xs font-medium text-gray-500 mb-1">Title</p>
                    <p className="text-sm font-semibold text-gray-800 p-3 bg-white rounded-lg border border-gray-100">
                      {inputValue?.title || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-500 mb-1">Description</p>
                    <div className="p-3 bg-white rounded-lg border border-gray-100 min-h-[80px] max-h-[150px] overflow-y-auto">
                      <p className="text-sm text-gray-700 whitespace-pre-line">
                        {inputValue?.desc || 'No description available'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Course Sections */}
              {inputValue?.content && inputValue.content.length > 0 && (
                <div className="bg-gray-50/80 rounded-xl border border-gray-200 overflow-hidden">
                  <div className="p-4 border-b border-gray-200 flex items-center gap-2">
                    <FiList className="w-4 h-4 text-[#715EFE]" />
                    <h3 className="text-sm font-semibold text-gray-700">Course Sections</h3>
                    <span className="ml-auto px-2 py-0.5 text-xs font-medium text-[#715EFE] bg-[#715EFE]/10 rounded-full">
                      {inputValue.content.length}
                    </span>
                  </div>
                  <div className="p-4 space-y-3">
                    {inputValue.content.map((item, index) => (
                      <div key={item._id || index} className="p-3 bg-white rounded-lg border border-gray-100 hover:border-[#715EFE]/30 transition-colors duration-200">
                        <div className="flex items-start gap-3">
                          <div className="flex-shrink-0 w-6 h-6 rounded-full bg-gradient-to-br from-[#855DF6]/10 to-[#715EFE]/10 flex items-center justify-center text-xs font-bold text-[#715EFE]">
                            {index + 1}
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-gray-800">{item.title}</p>
                            {item.desc && (
                              <p className="text-xs text-gray-500 mt-0.5">{item.desc}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Right Column - Creator & Details */}
            <div className="lg:col-span-1 space-y-6">
              {/* Creator Information */}
              <div className="bg-gray-50/80 rounded-xl border border-gray-200 overflow-hidden">
                <div className="p-4 border-b border-gray-200 flex items-center gap-2">
                  <FiUser className="w-4 h-4 text-[#715EFE]" />
                  <h3 className="text-sm font-semibold text-gray-700">Creator</h3>
                </div>
                <div className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-14 h-14 rounded-full overflow-hidden border-2 border-[#715EFE]/30 bg-gray-100 flex-shrink-0">
                      <img
                        src={inputValue.user?.profileImage || "/Img/user.png"}
                        alt={inputValue.user?.fullName || 'User'}
                        className="w-full h-full object-cover"
                        onError={(e) => e.target.src = "/Img/user.png"}
                      />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-800">
                        {inputValue?.user?.fullName || 'Unknown'}
                      </p>
                      <p className="text-xs text-gray-400">
                        Created {formatDate(inputValue?.createdAt)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Course Details */}
              <div className="bg-gray-50/80 rounded-xl border border-gray-200 overflow-hidden">
                <div className="p-4 border-b border-gray-200 flex items-center gap-2">
                  <FiInfo className="w-4 h-4 text-[#715EFE]" />
                  <h3 className="text-sm font-semibold text-gray-700">Course Details</h3>
                </div>
                <div className="p-4 space-y-2">
                  <InfoRow
                    icon={FiTag}
                    label="Type"
                    value={inputValue?.type === 1 ? 'Video' : 'Video'}
                  />
                  <InfoRow
                    icon={FiCheckCircle}
                    label="Status"
                    value={<StatusBadge isDisable={inputValue?.isDisable} />}
                  />
                  <InfoRow
                    icon={FiDollarSign}
                    label="Access"
                    value={inputValue?.isPaid ? 'Paid' : 'Free'}
                    color={inputValue?.isPaid ? 'text-emerald-600' : 'text-blue-600'}
                  />
                  {inputValue?.isPaid && (
                    <InfoRow
                      icon={FiDollarSign}
                      label="Price"
                      value={`$${inputValue?.price || 0}`}
                      color="text-emerald-600"
                    />
                  )}
                  <InfoRow
                    icon={FiUsers}
                    label="Purchases"
                    value={inputValue?.totalPurchase || 0}
                  />
                  <InfoRow
                    icon={FiStar}
                    label="Rating"
                    value={
                      <div className="flex items-center gap-1">
                        <span>{inputValue?.rating || 0}</span>
                        <AiFillStar className="text-yellow-400" />
                        <span className="text-xs text-gray-400 ml-1">
                          ({inputValue?.totalUserRating || 0})
                        </span>
                      </div>
                    }
                  />
                </div>
              </div>

              {/* Comments */}
              {inputValue?.comments && (
                <div className="bg-gray-50/80 rounded-xl border border-gray-200 overflow-hidden">
                  <div className="p-4 border-b border-gray-200 flex items-center gap-2">
                    <FiMessageCircle className="w-4 h-4 text-[#715EFE]" />
                    <h3 className="text-sm font-semibold text-gray-700">Comments</h3>
                  </div>
                  <div className="p-4">
                    <div className="p-3 bg-white rounded-lg border border-gray-100 min-h-[60px]">
                      <p className="text-sm text-gray-700 whitespace-pre-line">
                        {inputValue.comments}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Playlist Section */}
          {inputValue?.playlist && inputValue.playlist.length > 0 && (
            <div className="mt-6 bg-gray-50/80 rounded-xl border border-gray-200 overflow-hidden">
              <div className="p-4 border-b border-gray-200 flex items-center gap-2">
                <FiList className="w-4 h-4 text-[#715EFE]" />
                <h3 className="text-sm font-semibold text-gray-700">Playlist</h3>
                <span className="ml-auto px-2 py-0.5 text-xs font-medium text-[#715EFE] bg-[#715EFE]/10 rounded-full">
                  {inputValue.playlist.length}
                </span>
              </div>
              <div className="p-4">
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                  {inputValue.playlist.map((item, index) => (
                    <div
                      key={item._id || index}
                      className="group bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm hover:shadow-md hover:border-[#715EFE]/30 transition-all duration-300 cursor-pointer"
                      onClick={() => window.open(item.videoUrl, '_blank')}
                    >
                      <div className="relative overflow-hidden bg-gray-100">
                        <img
                          src={item.thumbUrl || "/Img/library.png"}
                          alt={item.title}
                          className="w-full h-40 object-cover group-hover:scale-105 transition-transform duration-300"
                          onError={(e) => e.target.src = "/Img/library.png"}
                        />
                        <div className="absolute bottom-2 right-2 px-2 py-1 bg-black/70 rounded-lg text-white text-xs font-medium">
                          <FiClock className="inline w-3 h-3 mr-1" />
                          {Math.floor(item.duration / 60)}:{Math.floor(item.duration % 60).toString().padStart(2, '0')}
                        </div>
                        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-black/40">
                          <FiPlayCircle className="w-12 h-12 text-white" />
                        </div>
                      </div>
                      <div className="p-3">
                        <h4 className="text-sm font-semibold text-gray-800 truncate">{item.title || 'Untitled'}</h4>
                        {item.desc && (
                          <p className="text-xs text-gray-500 mt-1 line-clamp-2">{item.desc}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContentViewModal;